import { Component } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router } from '@angular/router';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { GainsToastService } from '../../../services/gains-toast-service/gains-toast.service';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../../shared/services/auth-service/auth.service';
import { GapAccess, GapAccessService } from '../../../shared/services/gap-access/gap-access.service';
import { GapDenial, GapDeniedState } from '../../../shared/guards/gap-permission.guard';
import { GapOperation } from '../../../shared/permissions/gap-permissions';

/**
 * Where a user goes when GAP will not let them open a page (GapPermissionGuard, AccessDeniedInterceptor): no access to
 * GAP, access that could not be checked, or too low a level for that page.
 */
@Component({
  selector: 'error-page',
  imports: [CommonModule, NzButtonComponent, AngularSvgIconModule],
  templateUrl: './error-page.component.html',
  styleUrl: './error-page.component.scss'
})
export class ErrorPageComponent {
  private readonly _state: Partial<GapDeniedState> = history.state ?? {};
  /** The page the user asked for. */
  private readonly _returnUrl: string = this._state.returnUrl ?? '/client';
  /** The operation that page needs, if any. */
  private readonly _requires?: GapOperation = this._state.requires;

  reason: GapDenial = this._state.reason ?? 'no-access';
  checking = false;

  constructor(
    private readonly _router: Router,
    private readonly _location: Location,
    private readonly _authService: AuthService,
    private readonly _gapAccessService: GapAccessService,
    private readonly _toast: GainsToastService
  ) {}

  /** The level the page asked for needs; null when it needs no more than access to GAP. */
  get requiredLevel(): string | null {
    return this._requires ? this._gapAccessService.requiredLevel(this._requires) : null;
  }

  /**
   * To the page the user asked for, once authz lets them in at the level it needs. Asked here first: going there
   * without would show the page only for its guard to send the user back here.
   */
  tryAgain(): void {
    this.checking = true;
    this._gapAccessService
      .refresh()
      .pipe(finalize(() => (this.checking = false)))
      .subscribe(access => {
        const reason = this.denial(access);
        if (reason === null) {
          this._router.navigateByUrl(this._returnUrl);
          return;
        }
        this.reason = reason;
        this._toast.message(stillMessage(reason, this.requiredLevel), 'info');
      });
  }

  /**
   * Back where the user came from, for a page their level does not open; to that page's section when they came from
   * outside GAP (a link, a typed URL), as going back would leave GAP or do nothing.
   */
  goBack(): void {
    if (this._router.lastSuccessfulNavigation?.previousNavigation) {
      this._location.back();
    } else {
      this._router.navigateByUrl(sectionOf(this._returnUrl));
    }
  }

  /** To sign in with another account. */
  signOut(): void {
    this._authService.logout();
  }

  private denial(access: GapAccess): GapDenial | null {
    if (access.status === 'denied') {
      return 'no-access';
    }
    if (access.status === 'unreachable' || (this._requires && this.requiredLevel === null)) {
      return 'unreachable';
    }
    return this._requires && !this._gapAccessService.can(this._requires) ? 'level' : null;
  }
}

/** The section a page is in, its first segment: `/client/new` is in `/client`. */
function sectionOf(url: string): string {
  return '/' + (url.split(/[/?#]/).find(Boolean) ?? 'client');
}

function stillMessage(reason: GapDenial, level: string | null): string {
  switch (reason) {
    case 'level':
      return `You still need ${level} access to GAP for this page.`;
    case 'unreachable':
      return 'Your access to GAP still could not be checked.';
    default:
      return 'Still no access to GAP.';
  }
}
